import {
  toKonvaTextNodeProps,
  type SocialPostSceneBlockV1,
  type SocialPostSceneLeafBlockV1,
  type SocialPostSceneLogoBlockV1,
  type SocialPostSceneTextBlockV1,
} from "@runcheetah/shared";

export type SocialPostSceneRendererLinkedAsset = {
  blockId: string;
  signedUrl: string;
  width?: number | null;
  height?: number | null;
};

export type SocialPostSceneRendererLogoAsset = {
  objectKey: string;
  signedUrl: string;
  isPrimary?: boolean;
};

export type SocialPostSceneRendererBrandkit = {
  logo: {
    objectKey: string;
    signedUrl: string;
  } | null;
  fonts: Array<{
    family: string;
    source: "brand_confirmed" | "google_font";
    loadUrl: string;
    selectedVariant?: string;
    assetObjectKey?: string | null;
  }>;
};

type UnpositionedTextLine = {
  text: string;
  start: number;
  end: number;
};

const isSocialPostSceneLeafBlock = (
  block: SocialPostSceneBlockV1,
): block is SocialPostSceneLeafBlockV1 => block.type !== "group";

export const getVisibleSortedSocialPostSceneBlocks = (
  blocks: readonly SocialPostSceneBlockV1[],
): SocialPostSceneLeafBlockV1[] =>
  blocks
    .filter((block): block is SocialPostSceneLeafBlockV1 =>
      Boolean(block.visible && isSocialPostSceneLeafBlock(block)),
    )
    .toSorted((left, right) => left.zIndex - right.zIndex);

export const resolveSocialPostSceneLogoAsset = (input: {
  block: Pick<SocialPostSceneLogoBlockV1, "assetObjectKey">;
  brandkit: SocialPostSceneRendererBrandkit | null;
  logoAssets?: readonly SocialPostSceneRendererLogoAsset[];
}): SocialPostSceneRendererLogoAsset | null => {
  const logoAssets = input.logoAssets ?? [];
  const primaryLogo = logoAssets.find((logo) => logo.isPrimary) ?? null;

  if (input.block.assetObjectKey) {
    const selectedLogo = logoAssets.find((logo) => logo.objectKey === input.block.assetObjectKey);
    if (selectedLogo) {
      return selectedLogo;
    }

    if (logoAssets.length === 0 && input.brandkit?.logo) {
      return input.brandkit.logo;
    }
  }

  return primaryLogo ?? (logoAssets.length === 0 ? (input.brandkit?.logo ?? null) : null);
};

export const resolveSocialPostSceneTextRenderHeight = (input: {
  text: string;
  width: number;
  height: number;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
  padding: number;
  measureText: (value: string) => number;
}) => {
  const contentWidth = Math.max(0, input.width - input.padding * 2);
  const lineHeightPx = input.fontSize * input.lineHeight;
  const lines = wrapTextLines({
    text: input.text,
    contentWidth,
    letterSpacing: input.letterSpacing,
    measureText: input.measureText,
  });
  const contentHeight = Math.max(lineHeightPx, lines.length * lineHeightPx) + input.padding * 2;

  return Math.max(input.height, Math.ceil(contentHeight));
};

export const resolveSocialPostSceneTextNodeProps = (
  block: SocialPostSceneTextBlockV1,
  scale: number,
  measureText: (value: string) => number,
) => {
  const props = toKonvaTextNodeProps(block, scale, "local");

  return {
    ...props,
    height: resolveSocialPostSceneTextRenderHeight({
      text: block.text,
      width: props.width,
      height: props.height,
      fontSize: props.fontSize,
      lineHeight: block.lineHeight,
      letterSpacing: props.letterSpacing,
      padding: props.padding,
      measureText,
    }),
  };
};

const wrapTextLines = (input: {
  text: string;
  contentWidth: number;
  letterSpacing: number;
  measureText: (value: string) => number;
}): UnpositionedTextLine[] => {
  const paragraphs = input.text.split("\n");
  const lines: UnpositionedTextLine[] = [];
  let paragraphStart = 0;

  for (const paragraph of paragraphs) {
    if (!paragraph) {
      lines.push({ text: "", start: paragraphStart, end: paragraphStart });
      paragraphStart += 1;
      continue;
    }

    let lineStart = 0;

    while (lineStart < paragraph.length) {
      let bestEnd = lineStart + 1;

      while (bestEnd <= paragraph.length) {
        const candidate = paragraph.slice(lineStart, bestEnd);
        const candidateWidth = measureTextWithLetterSpacing(
          candidate,
          input.letterSpacing,
          input.measureText,
        );

        if (candidateWidth > input.contentWidth && bestEnd > lineStart + 1) {
          break;
        }

        bestEnd += 1;
      }

      const maxEnd = Math.min(paragraph.length, bestEnd - 1);
      const breakIndex = paragraph.lastIndexOf(" ", maxEnd);
      const lineEnd = breakIndex > lineStart && maxEnd < paragraph.length ? breakIndex : maxEnd;
      const nextLineStart = lineEnd === breakIndex ? lineEnd + 1 : lineEnd;

      lines.push({
        text: paragraph.slice(lineStart, lineEnd),
        start: paragraphStart + lineStart,
        end: paragraphStart + lineEnd,
      });
      lineStart = Math.max(nextLineStart, lineStart + 1);
    }

    paragraphStart += paragraph.length + 1;
  }

  return lines.length > 0 ? lines : [{ text: "", start: 0, end: 0 }];
};

const measureTextWithLetterSpacing = (
  value: string,
  letterSpacing: number,
  measureText: (value: string) => number,
) => measureText(value) + Math.max(0, value.length - 1) * letterSpacing;
